import * as React from 'react';
import type {IEventTemplate} from '../../../types/IEventTemplate';
import type {SequenceItem} from '../../../types/IEventTemplateMetadata';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {calculateDayNumbers} from '../../../utils/sequential';
import {SeparatorWrapper} from '../../helper-components/helper-components';
import {DayCard} from './day-card.tsx';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {SequenceDayItem} from './sequence-day-item';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa/qa';


interface SequenceTimelineProps {
  sequence: SequenceItem[];
  eventTemplate: IEventTemplate;
  testId?: string;
}

export const SequenceTimeline = ({
  sequence,
  eventTemplate,
  testId,
}: SequenceTimelineProps) => {
  const {t} = useLabelTranslation();

  if (sequence.length === 1) {
    return (
      <SequenceDayItem
        sequenceDay={sequence[0]}
        eventTemplate={eventTemplate}
        testId={generateTestId({base: testId, slot: 'sequence-day-item'})}
      />
    );
  }

  const dayNumberMap = calculateDayNumbers(sequence);
  let runningRoundCount = 0;

  return (
    <>
      {sequence.map((item, index) => {
        const nextItem = sequence?.[index + 1];
        const shouldShowSeparator =
          nextItem && (item?.type === 'day' || item?.type === 'gap');

        if (item.type === 'day') {
          const dayNumber = dayNumberMap.get(index) || 0;
          const roundNumberOffset = runningRoundCount;
          runningRoundCount += item.value.filter(
            (sequenceItem) => sequenceItem.type === 'round',
          ).length;
          return (
            <React.Fragment key={`day-${index}`}>
              <DayCard
                dayNumber={dayNumber}
                sequenceDay={item}
                eventTemplate={eventTemplate}
                roundNumberOffset={roundNumberOffset}
                index={index}
                testId={generateTestId({
                  base: testId,
                  slot: 'day-card',
                  index: index.toString(),
                })}
              />
              {shouldShowSeparator && (
                <SeparatorWrapper
                  testId={generateTestId({
                    base: testId,
                    slot: 'separator-wrapper',
                    index: index.toString(),
                  })}
                />
              )}
            </React.Fragment>
          );
        }
        if (item.type === 'gap') {
          const gapAmount = item.value?.amount || 0;
          const gapUnit = item.value?.unit || t('days', 'days');
          const gapLabel = `${gapAmount} ${gapUnit} ${t('Gap', 'Gap')}`;

          return (
            <React.Fragment key={`gap-${index}`}>
              <Chip
                semantic="information"
                testId={generateTestId({
                  base: testId,
                  slot: 'gap-chip',
                  index: index.toString(),
                })}
              >
                {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                {gapLabel}
              </Chip>
              {shouldShowSeparator && <SeparatorWrapper />}
            </React.Fragment>
          );
        }

        return null;
      })}
    </>
  );
};
